/**
 * @file strategyApi.ts
 * @description RTK Query API slice for strategy CRUD operations.
 */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { StrategyJSON } from '@forex-builder/shared-types';
import type { StrategyValidationIssue } from '@forex-builder/strategy-validator';
import type { RootState } from '../../../app/store.js';

export interface StrategyRecordClient {
  id: string;
  userId: string;
  name: string;
  description: string;
  symbol: string;
  timeframe: string;
  strategyJson: StrategyJSON;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface ListStrategiesResponse {
  success: boolean;
  data: {
    count: number;
    strategies: StrategyRecordClient[];
  };
}

export interface GetStrategyResponse {
  success: boolean;
  data: {
    strategy: StrategyRecordClient;
  };
}

export interface MutationStrategyResponse {
  success: boolean;
  message?: string;
  data?: {
    strategy: StrategyRecordClient;
    warnings?: StrategyValidationIssue[];
  };
}

import { createBaseQueryWithReauth } from '../../../shared/api/baseQueryWithReauth.js';

export const strategyApi = createApi({
  reducerPath: 'strategyApi',
  baseQuery: createBaseQueryWithReauth('/api/v1/strategies'),
  refetchOnMountOrArgChange: true,
  tagTypes: ['Strategy', 'BacktestHistory', 'BacktestSummary'],
  endpoints: (builder) => ({
    getStrategies: builder.query<ListStrategiesResponse, void>({
      query: () => '',
      providesTags: ['Strategy'],
    }),
    getStrategyById: builder.query<GetStrategyResponse, string>({
      query: (id) => `/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Strategy', id }],
    }),
    createStrategy: builder.mutation<MutationStrategyResponse, StrategyJSON>({
      query: (strategyJson) => ({
        url: '',
        method: 'POST',
        body: strategyJson,
      }),
      invalidatesTags: ['Strategy'],
    }),
    updateStrategy: builder.mutation<MutationStrategyResponse, { id: string; strategyJson: StrategyJSON }>({
      query: ({ id, strategyJson }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: strategyJson,
      }),
      invalidatesTags: (_result, _error, { id }) => ['Strategy', { type: 'Strategy', id }, 'BacktestHistory', 'BacktestSummary'],
    }),
    deleteStrategy: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Strategy'],
    }),
    duplicateStrategy: builder.mutation<MutationStrategyResponse, string>({
      query: (id) => ({
        url: `/${id}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: ['Strategy'],
    }),
  }),
});

export const {
  useGetStrategiesQuery,
  useGetStrategyByIdQuery,
  useCreateStrategyMutation,
  useUpdateStrategyMutation,
  useDeleteStrategyMutation,
  useDuplicateStrategyMutation,
} = strategyApi;
